import moment from "moment";
import { z } from "zod";


export const INITIATIVE_TYPE_OPTIONS = [
  { value: "new purchase", label: "New Purchase" },
  { value: "replace tool", label: "Replace Tool" },
  { value: "other", label: "Other" },
] as const;

export const INITIATIVE_TYPE_VALUES = INITIATIVE_TYPE_OPTIONS.map(
  (o) => o.value
) as readonly string[];

export function getInitiativeTypeDisplayLabel(beValue: string): string {
  const option = INITIATIVE_TYPE_OPTIONS.find(
    (o) => o.value === beValue?.toLowerCase()
  );
  return option?.label ?? beValue ?? "";
}

export const TARGETED_COMPLETION_OVER_1_YEAR_VALUE = "over_1_year";

export interface TargetedCompletionMonthOption {
  value: string;
  label: string;
  suffix?: string;
}


export function getDefaultPlannedDeliveryDate(): string {
  return moment().add(12, "months").startOf("month").format("YYYY-MM");
}


export function getTargetedCompletionMonthOptions(): TargetedCompletionMonthOption[] {
  const now = moment();
  const options: TargetedCompletionMonthOption[] = [];

  for (let i = 1; i <= 12; i++) {
    const monthDate = now.clone().add(i, "months").startOf("month");
    const value = monthDate.format("YYYY-MM");
    const monthLabel = monthDate.format("MMM, YYYY");
    const relative =
      i === 1
        ? `in ${Math.max(1, monthDate.diff(now, "days"))} days`
        : `in ${i} months`;
    options.push({ value, label: monthLabel, suffix: relative });
  }

  options.push({
    value: TARGETED_COMPLETION_OVER_1_YEAR_VALUE,
    label: "Over 1 year",
  });

  return options;
}


export function plannedDeliveryDateToPayload(formValue: string): string {
  if (formValue === TARGETED_COMPLETION_OVER_1_YEAR_VALUE) {
    return moment().add(13, "months").endOf("month").format("YYYY-MM-DD");
  }
  return moment(formValue, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
}


export function plannedDeliveryDateToFormValue(apiDate: string): string {
  if (!apiDate) return "";
  const m = moment(apiDate, "YYYY-MM-DD", true);
  if (!m.isValid()) return "";
  const yyyyMm = m.format("YYYY-MM");
  const monthOptions = getTargetedCompletionMonthOptions();
  const found = monthOptions.find((o) => o.value === yyyyMm);
  return found ? yyyyMm : TARGETED_COMPLETION_OVER_1_YEAR_VALUE;
}

export const createInitiativeSchema = z
  .object({
    title: z.string().min(1, "Title is required"),
    type: z
      .string()
      .min(1, "Type is required")
      .refine((val) => INITIATIVE_TYPE_VALUES.includes(val?.toLowerCase()), {
        message: "Invalid type",
      }),
    toolName: z.string().or(z.string().array()),
    summary: z
      .string()
      .min(1, "Summary is required"),
    plannedDeliveryDate: z.string().optional(),
    isPublic: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.type?.toLowerCase() !== "replace tool") return true;
      const toolNameStr = Array.isArray(data.toolName)
        ? data.toolName.filter(Boolean).join(", ").trim()
        : (data.toolName ?? "").trim();
      return toolNameStr.length > 0;
    },
    { message: "Please enter the tool you are replacing", path: ["toolName"] }
  );

export type CreateInitiativeFormData = z.infer<typeof createInitiativeSchema>;

export const defaultValues: CreateInitiativeFormData = {
  title: "",
  type: "new purchase",
  toolName: "",
  summary: "",
  plannedDeliveryDate: "",
  isPublic: true,
};
